import {NgClass, NgStyle} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';

// Type definitions for button variants
export type ButtonType =
  | 'primary'
  | 'secondary'
  | 'outlined'
  | 'load'
  | 'success'
  | 'icon'
  | 'image'
  | 'error'
  | 'delete';

// Type definitions for button sizes
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'auto';

// Interface defining button dimensions structure
interface ButtonDimensions {
  height: string;
  width: string;
}

// Interface defining button image
interface ButtonImage {
  src: string;
  alt?: string;
  width?: string;
}

/**
 * @author Harshit Kumar
 * @since Jan 23, 2025
 * */
@Component({
  selector: 'app-button',
  standalone: true,
  imports: [NgClass, MatIconModule, NgStyle],
  templateUrl: './button.component.html',
})
export class ButtonComponent {
  // Input property for button text
  @Input() label = '';

  // Input property for button identifier
  @Input() id: string = '';

  // Input property for button variant/style
  @Input() type: ButtonType = 'primary';

  // Input property to control button disabled state
  @Input() disabled = false;

  // Input property for optional icon name/class
  @Input() icon?: string = '';

  // Input property to control icon position (left or right of label)
  @Input() iconPosition: 'left' | 'right' = 'left';

  // Input property for button padding
  @Input() padding = '0.4rem 1.5rem';

  // Input property for button icon
  @Input() matIcon = '';

  // Input property to control button size
  @Input() buttonSize: ButtonSize = 'auto';

  // Button Image
  @Input() image?: ButtonImage;

  // Input property for native button type (submit, reset, button)
  @Input() buttonType: 'submit' | 'reset' | 'button' = 'button';

  // Default border radius for the button
  @Input() borderRadius: string = '0.375rem';

  // Default font size for button text
  @Input() fontSize: string = 'text-base';

  // Output event emitter for click events
  @Output() clickEvent: EventEmitter<any> = new EventEmitter();

  @Input() imageSource = '';

  constructor() {
    // Empty Constructor
  }

  /**
   * Handler for button click events
   * Emits the click event to parent component
   * @param event - The click event object
   */
  onClickButton(event: Event) {
    this.clickEvent.emit(event);
  }

  /**
   * Map defining button dimensions for different sizes
   * Contains predefined height and width values for each size variant
   */
  private readonly sizeMap: Record<ButtonSize, ButtonDimensions> = {
    xs: {height: '2.375rem', width: '6.25rem'}, // Extra small
    sm: {height: '2.5rem', width: '8.125rem'}, // Small
    md: {height: '2.5rem', width: '11.25rem'}, // Medium
    lg: {height: '2.5rem', width: '21.875rem'}, // Large  350px
    xl: {height: '2.8125rem', width: '25rem'}, // Extra Large
    auto: {height: '2.5rem', width: 'auto'}, // Auto width
  };

  /**
   * Getter that returns the computed button dimensions based on buttonSize input
   * Used to dynamically set button dimensions in template
   */
  get computedButtonDimensions(): ButtonDimensions {
    return this.sizeMap[this.buttonSize];
  }

  /**
   * Function to return button classes
   */
  getButtonClasses() {
    const typeClasses: Record<string, string> = {
      primary: 'bg-primary border-primary hover:bg-primaryMix',
      secondary: 'bg-outlinePrimary border-transparent hover:bg-disableMix',
      outlined: 'bg-white border border-primary hover:bg-whiteMix',
      load: 'bg-bpBGTertiary border-bpBGTertiary hover:bg-bpBGTertiaryMix',
      success: 'bg-success border-success hover:bg-successMix',
      error: 'bg-bpRedPrimary border-bpRedPrimary hover:bg-bpRedPrimaryMix',
      delete: 'border border-bpRedPrimary bg-white hover:bg-bpRedPrimaryMix text-bpRedPrimary hover:text-white'
    };

    return `${this.fontSize} ${typeClasses[this.type] || ''}
          ${this.disabled ? 'cursor-not-allowed opacity-50' : ''}`;
  }

}
